/**
 * Blocks and headings in a chapter body (requirement 0085, ADR-0154).
 *
 * A paragraph — lines up to a blank line — whose first line is `::: kind` or
 * `::: kind label` is a block: a note or an original text, which the reader
 * shows as a collapsed card and narration never speaks. An unlabelled marker of
 * the same kind opening the very next paragraph continues the block. A heading
 * is a line of one to six `#` and a space.
 *
 * The twin of `backend/internal/textblock` and of the app's `blocks.dart`, all
 * three held to `testdata/reader/blocks.json`. Indices count non-blank lines,
 * the paragraph rule (`studio/paragraphs.ts`), because that is what the views
 * lay out and what pictures and speakers are anchored to.
 */
import { trimSpace } from "../studio/paragraphs";

export type BlockKind = "note" | "original";

export interface Block {
  kind: BlockKind;
  label: string;
  /** The block's first marker line. */
  first: number;
  /** Its last line; below `first + 1` when it has no content. */
  last: number;
  /** Its marker lines, which are not content. */
  markers: number[];
}

const MARKER = /^:::[ \t]+([A-Za-z][A-Za-z0-9_-]*)(?:[ \t]+(.*?))?$/;
const HEADING = /^(#{1,6})[ \t]+(.*\S)$/;

/** What a marker line says, or null for any other line. */
export function markerOf(line: string): { kind: BlockKind; label: string } | null {
  const m = MARKER.exec(trimSpace(line));
  if (!m) return null;
  // Any other kind is a note: shown rather than lost.
  const kind: BlockKind = m[1]!.toLowerCase() === "original" ? "original" : "note";
  return { kind, label: trimSpace(m[2] ?? "") };
}

/** A heading line's level and text, or null. */
export function headingOf(line: string): { level: number; text: string } | null {
  const m = HEADING.exec(trimSpace(line));
  return m ? { level: m[1]!.length, text: trimSpace(m[2]!) } : null;
}

/**
 * A paragraph that is only a section number — "1", "12.", "۱۲" — is drawn as a
 * divider, not as text (the Tao Te Ching numbers its chapters this way). One to
 * three digits of any script and an optional full stop; anything more, "1. The
 * way…" or a year, is prose and stays as written. The number without its stop,
 * or null. The app's twin is `sectionNumberOf` in `blocks.dart`.
 */
export function sectionNumberOf(paragraph: string): string | null {
  const m = SECTION.exec(trimSpace(paragraph));
  return m ? m[1]! : null;
}

const SECTION = /^(\p{Nd}{1,3})\.?$/u;

export function blocksOf(body: string): Block[] {
  const out: Block[] = [];
  let open: Block | null = null;
  let index = -1;
  let opening = true;
  for (const line of body.split("\n")) {
    if (trimSpace(line) === "") {
      opening = true;
      continue;
    }
    index++;
    if (!opening) {
      if (open) open.last = index;
      continue;
    }
    opening = false;
    const marker = markerOf(line);
    if (!marker) {
      open = null;
    } else if (open && marker.label === "" && marker.kind === open.kind) {
      open.markers.push(index);
      open.last = index;
    } else {
      open = { ...marker, first: index, last: index, markers: [index] };
      out.push(open);
    }
  }
  return out;
}

/**
 * blockAt is, for each non-blank line, the block it belongs to, if any.
 */
export function blockAt(blocks: Block[]): Map<number, Block> {
  const at = new Map<number, Block>();
  for (const b of blocks) for (let i = b.first; i <= Math.max(b.first, b.last); i++) at.set(i, b);
  return at;
}

/**
 * The block's content as paragraphs of lines: a continuing marker starts a new
 * paragraph, so an original keeps one couplet per paragraph.
 */
export function blockParagraphs(block: Block, lines: string[]): string[][] {
  const out: string[][] = [];
  for (let i = block.first; i <= block.last; i++) {
    if (block.markers.includes(i)) {
      out.push([]);
      continue;
    }
    out[out.length - 1]!.push(lines[i]!);
  }
  return out.filter((p) => p.length > 0);
}
